import React from 'react'

import { fetchApi } from '@hozana/api/fetchApi'
import { useDispatch } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { useScreen } from '@hozana/screen/useScreen'
import { QA } from '@hozana/tests/constants'
import { EVENT_ORIGIN } from '@hozana/tracking/constants'

import { Div } from 'elements/layout/Div'
import { Separator } from 'elements/ui/Separator'

import { ICON } from 'config/icons'
import { addFlash } from 'general/actions'
import { PopoverButton } from 'general/structure/Popover/PopoverButton'
import { PAGE } from 'routes/constants'

import { CommunityThumbnail } from 'modules/community/components/CommunityThumbnail'
import { COMMUNITY_PAGE_TABS } from 'modules/community/constants'
import { getUserRole } from 'modules/community/functions'
import type { TCommunity } from 'modules/community/types'
import { editPublicationMutation } from 'modules/publication/queries'
import { addFavorite, deletePublication, duplicatePublication, removeFavorite } from 'modules/publication/routines'
import { useUser } from 'modules/user/hooks/useUser'

import type { TPublication } from '../types'

export type TPublicationOptionsProps = {
  publication: TPublication
  community: TCommunity
  isScheduled?: boolean
  isDraft?: boolean
  showCommunityActions: boolean
  closePopover?: VoidFunction
}

export const PublicationOptions: TFC<TPublicationOptionsProps> = ({
  publication,
  community,
  showCommunityActions,
  isScheduled,
  isDraft,
  closePopover,
}) => {
  const screen = useScreen()
  const router = useRouter()
  // Hide leave button if we haven't joined
  const { hasRole } = getUserRole(community)

  const me = useUser()
  const dispatch = useDispatch()

  const removeSchedule = () => {
    dispatch(
      fetchApi(editPublicationMutation({ publicationId: publication.id, isDraft: true, scheduledTime: null })),
    ).then(() => {
      dispatch(addFlash({ message: 'publication:publication.schedule-canceled' }))
      router.push(
        {
          pathname: PAGE.COMMUNITY_VIEW,
          query: {
            communityId: community.id,
            communitySlug: community.slug,
            tab: COMMUNITY_PAGE_TABS.SCHEDULED_AND_DRAFT,
          },
        },
        undefined,
        { locale: community.lang, shallow: true },
      )
    })
  }

  return (
    <>
      {/* Header of popover */}
      {showCommunityActions && (
        <>
          <Div p={{ xs: '1em 20px', md: '1em' }}>
            <CommunityThumbnail communityId={community.id} eventOrigin={EVENT_ORIGIN.PUBLICATION_OPTIONS_HEADER} />
          </Div>
          <Separator />
        </>
      )}
      {/* Edit the publication */}
      {(hasRole || me.admin) && (
        <PopoverButton
          icon={ICON.PENCIL}
          data-testid={QA.PUBLICATION_OPTIONS.EDIT_PUBLICATION}
          to={{
            pathname: PAGE.PUBLICATION_EDIT,
            query: {
              publicationId: publication.id,
              publicationSlug: publication.slug,
            },
          }}
          target={screen.lg || screen.md ? '_blank' : undefined}
        >
          trans:publication:publication.edit.edit
        </PopoverButton>
      )}
      {hasRole && (
        <PopoverButton
          icon={ICON.COPY}
          onClick={() => {
            closePopover()
            dispatch(duplicatePublication({ publication, router }))
          }}
        >
          trans:publication:publication.duplicate
        </PopoverButton>
      )}
      {/* Add in favorites */}
      {me.isLogged && !isScheduled && !isDraft && (
        <PopoverButton
          icon={publication.isFavorite ? ICON.BOOKMARK_ON : ICON.BOOKMARK_OFF}
          data-testid={QA.PUBLICATION_OPTIONS.REMOVE_FROM_FAVORITES}
          iconColor={publication.isFavorite ? 'yellow' : 'black'}
          onClick={() => {
            closePopover()
            dispatch(publication.isFavorite ? removeFavorite(publication) : addFavorite(publication))
          }}
        >
          {publication.isFavorite
            ? 'trans:publication:publication.remove-from-favorite'
            : 'trans:publication:publication.add-to-favorite.add'}
        </PopoverButton>
      )}
      {/* Switch to drafts / cancel schedule */}
      {me.isLogged && isScheduled && !isDraft && (
        <PopoverButton icon={ICON.CALENDAR} onClick={removeSchedule}>
          trans:publication:publication.scheduled-to-draft
        </PopoverButton>
      )}
      {/* Delete the publication */}
      {(hasRole || me.admin) && (
        <PopoverButton
          icon={ICON.TRASH}
          data-testid={QA.PUBLICATION_OPTIONS.DELETE_PUBLICATION}
          onClick={() => {
            closePopover()
            dispatch(deletePublication({ publication, router }))
          }}
        >
          trans:publication:publication.delete.delete
        </PopoverButton>
      )}
    </>
  )
}
